import React, { lazy, Suspense } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import FAQ from "@/components/FAQ";
import { LazySection } from "@/components/LazySection";
import { HelpCircle, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = lazy(() => import("@/components/Footer"));

const categories = [
  { label: "Eligibility", desc: "Is my property a good fit for Airbnb?" },
  {
    label: "Getting Started",
    desc: "What does the onboarding process look like?",
  },
  { label: "Operations", desc: "How does daily management work?" },
  { label: "Compliance", desc: "What about permits, taxes, and regulations?" },
  { label: "Financials", desc: "How do payments and reporting work?" },
  { label: "Remote Ownership", desc: "Can I manage from out of state?" },
];

const FAQPage = () => {
  const scrollToForm = () => {
    window.location.href = "/#lead-form";
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Frequently Asked Questions | Airbnb Management"
        description="Find answers to common questions about JNN Homes's Airbnb management services, pricing, compliance, and guest handling."
        canonical="https://www.jnnhomes.com/faq"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          url: "https://www.jnnhomes.com/faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "Is my property eligible for Airbnb?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We check zoning laws and specific permits for your area in VA & DC (Alexandria City, Arlington, Fairfax, Loudoun, Prince William, and Washington, D.C.) to ensure legal operation.",
              },
            },
            {
              "@type": "Question",
              name: "Do I need to answer guest messages myself?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No. The JNN Homes team is on call 24/7 to answer all guest inquiries within 5 minutes, from their initial question to check-out.",
              },
            },
            {
              "@type": "Question",
              name: "What if guests damage the property?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Airbnb provides AirCover insurance up to $1 million USD. JNN Homes files claims and requests compensation on your behalf if any incidents occur.",
              },
            },
            {
              "@type": "Question",
              name: "Can I just get information first without committing right away?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Submit your information and we will do a free market analysis so you can get a comprehensive overview before deciding.",
              },
            },
            {
              "@type": "Question",
              name: "Do I need a license to rent my home on Airbnb in Washington, D.C.?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. DC requires a short-term rental license from the Department of Licensing and Consumer Protection (DLCP). The home has to be your primary residence, which the District defines as a property that qualifies for the Homestead Deduction. It also has to be owned by an individual, not an LLC or corporation. A 'Short-Term Rental' license covers hosting while you live there, such as a spare room or English basement. A 'Vacation Rental' license covers renting the whole home while you're away, which DC limits to 90 nights a year unless you qualify for an exemption. Every stay is capped at 30 nights. We help you understand DC's license requirements, set up your listing with your license number displayed, and run bookings within the rules that apply to your home. We aren't attorneys and can't guarantee approval, so for anything specific to your situation, check with DLCP (dlcp.dc.gov) or your own legal or tax advisor.",
              },
            },
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-gradient-to-br from-background via-background to-muted/50 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
        <div className="container relative mx-auto px-4 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm font-semibold text-primary mb-6">
            <HelpCircle className="w-4 h-4" />
            Common Questions Answered
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            Everything You Want
            <br />
            <span className="text-primary italic">to Know First</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Before you commit to anything, we want you to feel completely
            informed. Here are the questions every VA & DC homeowner asks us.
          </p>
        </div>
      </section>

      {/* Category chips */}
      <section className="py-10 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 max-w-5xl mx-auto">
            {categories.map((cat, i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-xl p-4 text-center hover:border-primary/30 transition-colors cursor-default"
              >
                <div className="font-semibold text-foreground text-sm mb-1">
                  {cat.label}
                </div>
                <div className="text-xs text-muted-foreground leading-snug">
                  {cat.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <FAQ includeDCLicense />

      {/* Still have questions */}
      <section className="py-20 bg-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Still Have a Specific Question?
          </h2>
          <p className="text-muted-foreground mb-8">
            Call us directly or submit your property info and we'll answer every
            question during your free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToForm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full"
            >
              Get Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a href="tel:7038905425">
              <Button
                variant="outline"
                className="font-bold text-lg px-8 py-6 h-auto rounded-full w-full sm:w-auto"
              >
                <Phone className="w-5 h-5 mr-2" />
                (703) 890-5425
              </Button>
            </a>
          </div>
        </div>
      </section>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={<div className="h-40 bg-card/20" />}
      >
        <Suspense fallback={<div className="h-40 bg-card/20" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default FAQPage;
